import { api } from "../../lib";
import {
  requireDateOnlyString,
  type DateOnlyString,
} from "../../utils/dateOnly";

export type CommunicationChannel = "email" | "hl7" | "fax" | "sms" | "voice";

export type CommunicationStatus =
  | "pending"
  | "sent"
  | "delivered"
  | "failed"
  | "bounced";

export type CommunicationRoutingDecision =
  | "redirected"
  | "bypassed"
  | "direct"
  | "blocked"
  | "unknown";

export type CommunicationRoutingMatch =
  | "case"
  | "physician"
  | "practice"
  | "surgery_center"
  | "portal_grant";

export interface CommunicationRoutingPrefix {
  id: string | null;
  code: string | null;
}

export interface CommunicationLogTriggeredBy {
  id: string;
  name: string;
}

export interface CommunicationLogListItem {
  id: string;
  channel: CommunicationChannel;
  communicationType: string;
  recipient: string;
  subject: string | null;
  status: CommunicationStatus;
  sentAt: string;
  deliveredAt: string | null;
  caseNumber: string | null;
  triggeredBy: CommunicationLogTriggeredBy | null;
  routingDecision: CommunicationRoutingDecision | null;
  actualRecipient: string | null;
  routingReason: string | null;
  matchedPrefix: CommunicationRoutingPrefix | null;
  matchedVia: CommunicationRoutingMatch | null;
}

export interface CommunicationLogDetail extends CommunicationLogListItem {
  body: string | null;
  failureReason: string | null;
  providerMessageId: string | null;
  metadata: Record<string, unknown> | null;
  case: { id: string; caseNumber: string | null } | null;
  patient: { id: string; name: string } | null;
  recipientUser: { id: string; name: string } | null;
}

export interface CommunicationLogFilters {
  channel?: CommunicationChannel | "";
  communicationType?: string;
  status?: CommunicationStatus | "";
  routingDecision?: CommunicationRoutingDecision | "";
  recipient?: string;
  caseNumber?: string;
  caseId?: string;
  startDate?: DateOnlyString;
  endDate?: DateOnlyString;
  page?: number;
  limit?: number;
}

export interface PaginatedCommunicationLogs {
  data: CommunicationLogListItem[];
  total: number;
  page: number;
  limit: number;
}

function appendFilterParams(
  params: URLSearchParams,
  filters: CommunicationLogFilters
) {
  if (filters.channel) params.set("channel", filters.channel);
  if (filters.communicationType)
    params.set("communicationType", filters.communicationType);
  if (filters.status) params.set("status", filters.status);
  if (filters.routingDecision)
    params.set("routingDecision", filters.routingDecision);
  if (filters.recipient) params.set("recipient", filters.recipient);
  if (filters.caseNumber) params.set("caseNumber", filters.caseNumber);
  if (filters.caseId) params.set("caseId", filters.caseId);
  if (filters.startDate)
    params.set(
      "startDate",
      requireDateOnlyString(filters.startDate, "Communication start date")
    );
  if (filters.endDate)
    params.set(
      "endDate",
      requireDateOnlyString(filters.endDate, "Communication end date")
    );
}

export const communicationLogService = {
  list: async (filters: CommunicationLogFilters = {}) => {
    const params = new URLSearchParams();
    appendFilterParams(params, filters);
    if (filters.page) params.set("page", String(filters.page));
    if (filters.limit) params.set("limit", String(filters.limit));

    const query = params.toString();
    return api.get<PaginatedCommunicationLogs>(
      `/communication-logs${query ? `?${query}` : ""}`
    );
  },

  getById: async (id: string) => {
    return api.get<CommunicationLogDetail>(`/communication-logs/${id}`);
  },

  exportUrl: (filters: CommunicationLogFilters = {}): string => {
    const params = new URLSearchParams();
    appendFilterParams(params, filters);
    const base = process.env.REACT_APP_API_BASE_URL || "";
    const query = params.toString();
    return `${base}/lis/communication-logs/export${query ? `?${query}` : ""}`;
  },
};
